import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import {
  extractFleetMediaFilename,
  getFleetMediaUrl,
  getFleetUploadRelativeUrl,
} from "../../../../../api/fleet-management/api";
import {
  useDeleteMediaMutation,
  useUploadMediaMutation,
} from "../../../../../api/fleet-management/media/media.hooks";

const PAYMENT_TYPE_OPTIONS = [
  { value: "advance", label: "Advance" },
  { value: "remaining", label: "Remaining" },
  { value: "refund", label: "Refund" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "online", label: "Online" },
  { value: "cash", label: "Cash" },
  { value: "direct-bank-transfer", label: "Direct Bank Transfer" },
];

const PaymentFormModal = ({
  show,
  isEditMode,
  form,
  errors,
  isSubmitting,
  onClose,
  onSubmit,
  onFieldChange,
}) => {
  const [mediaError, setMediaError] = useState("");
  const { mutateAsync: uploadMedia, isLoading: isUploadingReceipt } = useUploadMediaMutation();
  const { mutateAsync: deleteMedia, isLoading: isDeletingReceipt } = useDeleteMediaMutation();

  const isBusy = isSubmitting || isUploadingReceipt || isDeletingReceipt;
  const requiresTransactionId = form.mode === "online" || form.mode === "direct-bank-transfer";

  useEffect(() => {
    if (form.receipt) {
      setMediaError("");
    }
  }, [form.receipt]);

  const handleUploadReceipt = async (file) => {
    if (!file) return;

    setMediaError("");

    try {
      const existingFilename = extractFleetMediaFilename(form.receipt);
      if (existingFilename) {
        await deleteMedia({ filename: existingFilename });
      }

      const uploadResult = await uploadMedia(file);
      const relativePath = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!relativePath) {
        throw new Error("Upload response did not include media path.");
      }

      onFieldChange("receipt", relativePath);
    } catch (uploadError) {
      setMediaError(uploadError?.response?.data?.message || "Failed to upload receipt.");
    }
  };

  const handleRemoveReceipt = async () => {
    if (!form.receipt) return;

    setMediaError("");

    const filename = extractFleetMediaFilename(form.receipt);
    if (!filename) {
      onFieldChange("receipt", "");
      return;
    }

    try {
      await deleteMedia({ filename });
      onFieldChange("receipt", "");
    } catch (deleteError) {
      setMediaError(deleteError?.response?.data?.message || "Failed to remove receipt.");
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton={!isBusy}>
        <Modal.Title>{isEditMode ? "Edit Payment" : "Add Payment"}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Payment Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={form.paymentType}
                  onChange={(e) => onFieldChange("paymentType", e.target.value)}
                  isInvalid={!!errors.paymentType}
                  disabled={isBusy}
                >
                  <option value="">Select payment type</option>
                  {PAYMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.paymentType}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Mode <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={form.mode}
                  onChange={(e) => onFieldChange("mode", e.target.value)}
                  isInvalid={!!errors.mode}
                  disabled={isBusy}
                >
                  <option value="">Select mode</option>
                  {PAYMENT_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.mode}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={form.amount}
                  onChange={(e) => onFieldChange("amount", e.target.value)}
                  isInvalid={!!errors.amount}
                  disabled={isBusy}
                  placeholder="5000"
                />
                <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Payment Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={form.paymentDate}
                  onChange={(e) => onFieldChange("paymentDate", e.target.value)}
                  isInvalid={!!errors.paymentDate}
                  disabled={isBusy}
                />
                <Form.Control.Feedback type="invalid">{errors.paymentDate}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            {requiresTransactionId && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Transaction ID <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.transactionId}
                    onChange={(e) => onFieldChange("transactionId", e.target.value)}
                    isInvalid={!!errors.transactionId}
                    disabled={isBusy}
                    placeholder="TRX-12345"
                  />
                  <Form.Control.Feedback type="invalid">{errors.transactionId}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}

            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) => onFieldChange("description", e.target.value)}
                  isInvalid={!!errors.description}
                  disabled={isBusy}
                  placeholder="Enter payment details"
                />
                <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>Receipt</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={isBusy}
                  onChange={(e) => handleUploadReceipt(e.target.files?.[0])}
                />
                <Form.Text className="text-muted">Optional: upload receipt/proof of payment.</Form.Text>
              </Form.Group>

              {form.receipt && (
                <div className="d-flex align-items-center justify-content-between border rounded p-2 mt-2">
                  <a
                    href={getFleetMediaUrl(form.receipt)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-truncate me-2"
                  >
                    View current receipt
                  </a>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={handleRemoveReceipt}
                    disabled={isBusy}
                  >
                    {isDeletingReceipt ? "Removing..." : "Remove"}
                  </Button>
                </div>
              )}

              {isUploadingReceipt && (
                <small className="text-muted d-block mt-2">
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Uploading receipt...
                </small>
              )}
              {!!(mediaError || errors.receipt) && (
                <div className="text-danger small mt-1">{mediaError || errors.receipt}</div>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isBusy}>
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {isEditMode ? "Updating..." : "Saving..."}
              </>
            ) : isEditMode ? (
              "Update Payment"
            ) : (
              "Save Payment"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentFormModal;
